import * as Sentry from '@sentry/react';
import { bifrostEnv, bitbucketCommit } from '../config';
import { sendErrorAnalytics } from '../analytics/errorAnalytics';
import { activeExperiments } from '../experiment/experiments';
import { getExperimentNoExposure } from '../experiment/experimentClient';
import { type UserAccount } from '../clients/gateway-client/types';
import { isIgnoredHttpClientError } from './ignoredHttpClientErrors';
import { isForeignExtensionError } from './foreignExtensionErrors';

const getSentryDsn = (env: string): string => {
	if (env === 'dev') {
		return 'https://f53c67cb2614beaabdff4ee6a84aaa95@o55978.ingest.us.sentry.io/4507891849625600';
	}
	return 'https://9cf62308d0228b7d1d63b292839a8c37@o55978.ingest.us.sentry.io/4507891849625600';
};

const getRovoExtensionVersion = (): string => {
	const version = new URLSearchParams(window.location.search).get('version');
	return version ?? 'unknown';
};
export const sanitizeValue = (value: string | undefined): string | undefined => {
	if (value === undefined || value === '') {
		return value;
	}
	return '[SANITIZED]';
};

const sensitiveParamKeys = Object.freeze(['rovoChatPrompt']);
const sanitizeQueryParams = (
	queryString: Sentry.RequestEventData['query_string'],
): Sentry.RequestEventData['query_string'] => {
	if (!queryString) {
		return;
	}

	if (Array.isArray(queryString)) {
		return queryString.map(([key, value]: [string, string]): [string, string] => [
			key,
			sensitiveParamKeys.includes(key) ? (sanitizeValue(value) ?? '') : value,
		]);
	} else if (typeof queryString === 'string') {
		const params = new URLSearchParams(queryString);
		sensitiveParamKeys.forEach((key) => {
			if (params.has(key)) {
				params.set(key, sanitizeValue(params.get(key) ?? '') ?? '');
			}
		});
		return params.toString();
	}
	const sanitizedQuery: Record<string, string> = {};
	for (const [key, value] of Object.entries(queryString)) {
		sanitizedQuery[key] = sensitiveParamKeys.includes(key) ? (sanitizeValue(value) ?? '') : value;
	}
	return sanitizedQuery;
};

const sanitizeUrl = (url: string | undefined): string | undefined => {
	if (!url || !url.includes('?')) {
		return url;
	}

	const [baseUrl, queryString] = url.split('?');
	const sanitizedQueryString = sanitizeQueryParams(queryString);

	return sanitizedQueryString ? `${baseUrl}?${sanitizedQueryString}` : baseUrl;
};

const hasUserAccountKeys = (obj: unknown): obj is Partial<UserAccount> => {
	if (!obj || typeof obj !== 'object') {
		return false;
	}
	const userAccountKeys: (keyof UserAccount)[] = ['email', 'name', 'nickname', 'picture'];
	return userAccountKeys.some((key) => key in obj);
};

export const sanitizeEventPII = (event: Sentry.ErrorEvent): Sentry.ErrorEvent => {
	const sanitizedEvent = {
		...event,
	};

	sanitizedEvent.contexts = {
		...event.contexts,
		response: {
			...event.contexts?.response,
			cookies: undefined,
			headers: undefined,
		},
		'Atlassian Trace': {
			trace_id: event.contexts?.response?.headers?.['x-trace-id'],
			request_id: event.contexts?.response?.headers?.['atl-request-id'],
		},
	};

	if (sanitizedEvent.extra && hasUserAccountKeys(sanitizedEvent.extra)) {
		sanitizedEvent.extra = {
			...sanitizedEvent.extra,
			email: sanitizeValue(sanitizedEvent.extra.email),
			name: sanitizeValue(sanitizedEvent.extra.name),
			nickname: sanitizeValue(sanitizedEvent.extra.nickname),
			picture: sanitizeValue(sanitizedEvent.extra.picture),
		};
	}

	if (event.request?.method) {
		sanitizedEvent.message = `${event.contexts?.response?.status_code} - Failed to ${event.request.method} ${event.request.url ? new URL(event.request.url).pathname : ''}`;
	}

	sanitizedEvent.request = {
		...event.request,
		url: sanitizeUrl(event.request?.url),
		headers: {
			'User-Agent': event.request?.headers?.['User-Agent'] ?? '',
		},
		cookies: undefined,
		query_string: sanitizeQueryParams(event.request?.query_string),
	};

	if (sanitizedEvent.exception?.values) {
		sanitizedEvent.exception.values = sanitizedEvent.exception.values.map((value) => {
			if (value.type === 'Error' && value.mechanism?.type === 'http.client') {
				return {
					...value,
					value: sanitizedEvent.message,
				};
			}

			return value;
		});
	}

	return sanitizedEvent;
};

const processBeforeSendEvent = (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
	// Drop errors thrown inside *other* browser extensions injected into our page.
	// Sentry's addEventListener instrumentation catches exceptions from third-party
	// extensions' listeners on the shared window and misattributes them to us. See
	// foreignExtensionErrors.ts.
	if (isForeignExtensionError(event)) {
		return null;
	}

	// Keep wide httpClientIntegration monitoring (400-599) but drop a curated set
	// of expected HTTP responses the integration reports as errors (e.g. a 404
	// that is really a lookup's negative answer). See ignoredHttpClientErrors.ts.
	if (isIgnoredHttpClientError(event)) {
		return null;
	}

	const sanitizedEvent = sanitizeEventPII(event);

	// Send error analytics before sending to Sentry
	try {
		sendErrorAnalytics(sanitizedEvent);
	} catch {
		// do nothing
	}

	return sanitizedEvent;
};

export const initializeSentry = (): void => {
	Sentry.init({
		dsn: getSentryDsn(bifrostEnv),
		environment: bifrostEnv,
		release: `rovo-extension-frontend@${bitbucketCommit}`,
		sendDefaultPii: true,
		integrations: [
			Sentry.httpClientIntegration({
				failedRequestStatusCodes: [
					[400, 400],
					[402, 402],
					[404, 599],
				],
			}),
		],
		beforeSend: processBeforeSendEvent,
		normalizeDepth: 5,
	});

	Sentry.setTag('rovoExtensionVersion', getRovoExtensionVersion());
};

export const setSentryUser = (accountId: string): void => {
	Sentry.setUser({
		id: accountId,
	});
};

export const setSentrySiteId = (siteId?: string): void => {
	Sentry.setTag('siteId', siteId);
};

export const clearSentryUser = (): void => {
	Sentry.setUser(null);
};

export const setSentryExperiments = (): void => {
	activeExperiments.forEach((experiment) => {
		const value = getExperimentNoExposure({
			experimentName: experiment.experimentName,
			parameterName: experiment.parameterName,
			defaultValue: false,
		});

		Sentry.setTag(`exp.${experiment.experimentName}`, value ? 'enabled' : 'disabled');
	});
};
